import { AssetDTO, EntityRefUtil } from '@gearbelt/global';
import { zodResolver } from '@hookform/resolvers/zod';
import { GearbeltAssetType } from '@site-mate/sitemate-global-shared/lib';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { AssetCreatableMultiSelectField, AssetInputField, AssetSelectField, AssetTextareaField, extendedAssetFormSchema } from '@/components/assets/asset-form';
import SkidSteerIcon from '@/components/icons/skid-steer';
import { Form } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { UserAvatarLabel } from '@/components/users/user-avatar-label';
import { initials } from '@/lib/utils';
import { getAvatarBackgroundColorClass } from '@/utils/avatar';
import { convertImageUrl } from '@/utils/image-url';

export function AWAAssetOverview({ asset }: { asset: AssetDTO }) {
  const form = useForm<z.infer<typeof extendedAssetFormSchema>>({
    resolver: zodResolver(extendedAssetFormSchema),
    defaultValues: {
      class: asset.class || '',
      comments: asset.comments || '',
      engineNumber: asset.engineNumber || '',
      internalId: asset.internalId || '',
      manufacturer: asset.manufacturer || '',
      model: asset.model || '',
      registration: asset.registration || '',
      serialNumber: asset.serialNumber || '',
      vin: asset.chassisVin || '',
      locationId: asset.location?.id || '',
      type: asset.type === GearbeltAssetType.Assets || asset.type === GearbeltAssetType.ToolsAndEquipment ? asset.type : undefined,
      categoryId: asset.category?.id || '',
      tags: asset?.tags?.map((tag) => ({ label: tag.tag, value: tag.tag })) ?? [],
      year: asset.year ? asset.year.toString() : undefined,
    },
  });

  return (
    <div className="flex w-full flex-col items-start p-0">
      <Form {...form}>
        <form className="w-full">
          <div className="bg-grey-1 text-default-text flex h-[31px] w-full flex-row items-center px-2 py-1 text-sm font-medium">Asset management</div>
          <div className="flex w-full flex-col items-start gap-4 bg-white p-5">
            <div className="flex w-full flex-col gap-1">
              <label className="font-base text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Photo:</label>
              <div className="flex max-h-[200px] max-w-full">
                {asset?.photoUrl ? (
                  <img src={convertImageUrl(asset.photoUrl)} alt="Asset" className="object-fit max-h-[200px] max-w-full rounded-md" />
                ) : (
                  <div className="border-grey-3 bg-grey-1 flex h-[100px] w-[100px] flex-col items-center justify-center gap-1 rounded-md border-2 p-0">
                    <SkidSteerIcon size={30} />
                    <div className="text-default-text w-[100px] text-center text-xs font-normal">No Image available</div>
                  </div>
                )}
              </div>
            </div>
            <AssetSelectField<true>
              control={form.control}
              name="locationId"
              label="Location"
              options={[{ label: asset.location?.name ?? '', value: asset.locationId ?? '' }]}
              labelSize="base"
              className="w-full"
              disabled
            />
            <div className="flex w-full flex-col gap-1">
              <Label className="text-base">Operator</Label>
              {asset.operatedBy ? (
                <UserAvatarLabel
                  value={EntityRefUtil.createUserFullNameRef(asset.operatedBy)}
                  operatedByFullInitials={initials(EntityRefUtil.createUserFullNameRef(asset.operatedBy))}
                  avatarUrl={asset.operatedBy.avatarUrl || ''}
                  avatarBackgroundColorClass={getAvatarBackgroundColorClass(asset.operatedBy.firstName || '')}
                />
              ) : (
                <span className="text-secondary-text text-sm">-</span>
              )}
            </div>
          </div>
          <div className="bg-grey-1 text-default-text flex h-[31px] w-full flex-row items-center px-2 py-1 text-sm font-medium">Asset details</div>
          <div className="flex w-full flex-col items-start gap-4 bg-white p-5">
            <AssetInputField<true> className="w-full" control={form.control} name="internalId" label="Asset ID" placeholder="" labelSize="base" disabled />
            <AssetSelectField<true>
              control={form.control}
              name="type"
              label="Asset Type"
              options={Object.values(GearbeltAssetType).map((label) => ({ label, value: label })) ?? []}
              labelSize="base"
              className="w-full"
              disabled
            />
            <AssetSelectField<true>
              control={form.control}
              name="categoryId"
              label="Asset Category"
              options={[{ label: asset.category?.name ?? '', value: asset.categoryId ?? '' }]}
              labelSize="base"
              className="w-full"
              disabled
            />
            <AssetSelectField<true>
              control={form.control}
              name="manufacturer"
              label="Manufacturer"
              options={[{ label: asset.manufacturer ?? '', value: asset.manufacturer ?? '' }]}
              labelSize="base"
              className="w-full"
              disabled
            />
            <AssetInputField<true> control={form.control} name="model" label="Model" placeholder="" labelSize="base" className="w-full" disabled />
            <AssetInputField<true> control={form.control} name="class" label="Class" placeholder="" labelSize="base" className="w-full" disabled />
            <AssetSelectField<true>
              control={form.control}
              name="year"
              label="Year"
              options={[{ label: String(asset.year ?? ''), value: String(asset.year ?? '') }]}
              labelSize="base"
              className="w-full"
              disabled
            />
            <AssetInputField<true>
              control={form.control}
              name="registration"
              label="Registration"
              placeholder=""
              labelSize="base"
              className="w-full"
              disabled
            />
            <AssetInputField<true>
              control={form.control}
              name="serialNumber"
              label="Serial Number"
              placeholder=""
              labelSize="base"
              className="w-full"
              disabled
            />
            <AssetInputField<true>
              control={form.control}
              name="engineNumber"
              label="Engine Number"
              placeholder=""
              labelSize="base"
              className="w-full"
              disabled
            />
            <AssetInputField<true> control={form.control} name="vin" label="Chassis/VIN" placeholder="" labelSize="base" className="w-full" disabled />
            <AssetTextareaField<true> control={form.control} name="comments" label="Comments" placeholder="" className="w-full" labelSize="base" disabled />
            <AssetCreatableMultiSelectField<true>
              control={form.control}
              name="tags"
              label="Tags"
              placeholder=""
              className="w-full"
              controlRootClassName="h-full"
              controlTriggerClassName="h-full !items-start p-2"
              labelSize="base"
              defaultOptions={[]}
              disabled
            />
          </div>
        </form>
      </Form>
    </div>
  );
}
